<template>
    <div class="capacity-date-picker-wrap">
        <!-- Date Trigger Button -->
        <b-button
            :id="triggerId"
            variant="outline-secondary"
            size="sm"
            class="capacity-date-trigger-btn d-flex align-items-center justify-content-between w-100 text-left"
            :class="{
                'border-success text-success': isMultiMode && isAllocationComplete,
                'border-warning text-warning-contrast': isMultiMode && !isAllocationComplete,
                'border-warning text-warning-contrast': !isMultiMode && selectedDayHasGap,
                'border-success': !isMultiMode && selectedDayIsFull
            }"
            :disabled="disabled"
            @click="togglePopover"
            :title="triggerTitle"
            data-test="btn-picker-capacity-date"
        >
            <div class="d-flex align-items-center text-truncate mr-1">
                <i class="bi mr-1 flex-shrink-0" :class="isMultiMode ? 'bi-calendar-range text-info' : 'bi-calendar3 text-info'"></i>
                <span class="font-weight-bold date-label text-truncate">{{ triggerDisplayLabel }}</span>
                <i v-if="!disabled" class="bi bi-pencil-square ml-1 text-muted flex-shrink-0 extra-small" aria-hidden="true"></i>
            </div>
            <span
                v-if="isMultiMode"
                class="badge badge-pill flex-shrink-0 capacity-badge-pill"
                :class="isAllocationComplete ? 'badge-success' : 'badge-warning'"
                :title="isAllocationComplete ? 'Fully allocated across dates' : `${remainingHours}h remaining to allocate`"
            >
                {{ isAllocationComplete ? '✓ Full' : `${remainingHours}h left` }}
            </span>
            <span
                v-else-if="selectedDayBadge"
                class="badge badge-pill flex-shrink-0 capacity-badge-pill"
                :class="selectedDayBadge.badgeClass"
                :title="selectedDayBadge.title"
            >
                {{ selectedDayBadge.text }}
            </span>
        </b-button>

        <!-- Multi-Date Allocated Mini Chips (when collapsed in table row) -->
        <div v-if="isMultiMode && currentAllocations.length > 0" class="mini-allocations-chips mt-1 d-flex flex-wrap align-items-center">
            <span
                v-for="alloc in currentAllocations"
                :key="alloc.date"
                class="badge badge-light border mr-1 mb-1 mini-alloc-chip"
                :title="`${alloc.date}: +${alloc.hours}h`"
            >
                {{ formatChipDate(alloc.date) }} <strong class="text-primary">+{{ alloc.hours }}h</strong>
            </span>
        </div>

        <!-- Calendar Popover -->
        <b-popover
            :target="triggerId"
            :show="isPopoverOpen"
            @update:show="isPopoverOpen = $event"
            triggers="manual"
            placement="bottom"
            container="body"
            custom-class="capacity-calendar-popover shadow-lg"
        >
            <div
                class="capacity-calendar-container p-2"
                :class="{ 'multi-calendar-container': isMultiMode }"
                :data-theme="currentTheme"
            >
                <!-- Multi-Mode Action Bar -->
                <div v-if="isMultiMode" class="multi-header-bar pb-2 mb-2 border-bottom">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="font-weight-bold small text-truncate">
                            <i class="bi bi-distribute-horizontal mr-1 text-primary"></i>
                            Allocate +{{ deltaHours }}h across capacity gaps
                        </span>
                        <div class="d-flex align-items-center">
                            <b-button
                                size="sm"
                                variant="primary"
                                class="px-2 py-0 mr-1 font-weight-bold"
                                @click.stop="runAutoFillGaps"
                                title="Automatically distribute hours into empty and under-capacity days"
                                data-test="btn-auto-fill-gaps"
                            >
                                <i class="bi bi-magic mr-1"></i>Auto-Fill
                            </b-button>
                            <b-button
                                size="sm"
                                variant="outline-secondary"
                                class="px-2 py-0"
                                @click.stop="resetAllocations"
                                title="Clear all allocations"
                                data-test="btn-reset-allocations"
                            >
                                <i class="bi bi-arrow-counterclockwise"></i>
                            </b-button>
                        </div>
                    </div>

                    <!-- Progress Bar & Allocation Status -->
                    <div class="allocation-progress-card p-2 rounded mb-2">
                        <div class="d-flex justify-content-between align-items-center extra-small font-weight-bold mb-1">
                            <span>
                                Allocated: <strong class="text-info">{{ totalAllocatedHours }}h</strong> / {{ deltaHours }}h
                            </span>
                            <span :class="isAllocationComplete ? 'text-success' : 'text-warning'">
                                <i :class="isAllocationComplete ? 'bi bi-check-circle-fill mr-1' : 'bi bi-hourglass-split mr-1'"></i>
                                {{ isAllocationComplete ? '100% Filled' : `${remainingHours}h remaining` }}
                            </span>
                        </div>
                        <b-progress
                            :value="percentAllocated"
                            :variant="progressVariant"
                            height="6px"
                            class="mb-1"
                        ></b-progress>

                        <!-- Overtime Toggle & Deficit Alert -->
                        <div class="d-flex justify-content-between align-items-center mt-1">
                            <b-form-checkbox
                                v-model="localAllowOvertime"
                                size="sm"
                                class="extra-small text-muted font-weight-normal"
                                @change="onOvertimeToggled"
                            >
                                Allow Overtime logging
                            </b-form-checkbox>
                            <span v-if="gapsAvailableSummary !== null" class="extra-small text-muted" :title="`Total available capacity gaps up to today: ${gapsAvailableSummary}h`">
                                Gaps: <strong>{{ gapsAvailableSummary }}h</strong>
                            </span>
                        </div>

                        <!-- Notice Banner if Gaps Exhausted or Day Full -->
                        <div v-if="multiNotice" class="alert alert-warning py-1 px-2 mb-0 mt-1 extra-small border-warning d-flex align-items-center">
                            <i class="bi bi-exclamation-triangle-fill mr-1 text-warning flex-shrink-0"></i>
                            <span>{{ multiNotice }}</span>
                        </div>
                    </div>
                </div>

                <!-- Header with Month Navigation -->
                <div class="d-flex justify-content-between align-items-center mb-2 calendar-nav-bar">
                    <b-button
                        variant="outline-secondary"
                        size="sm"
                        class="px-2 py-0"
                        @click.stop="navigateMonths(-1)"
                        :disabled="isPrevNavDisabled"
                        title="Previous month"
                    >
                        &laquo;
                    </b-button>
                    <span class="font-weight-bold small active-month-label text-center px-1">
                        {{ currentMonthYearLabel }}
                    </span>
                    <div class="d-flex align-items-center">
                        <b-button
                            variant="outline-primary"
                            size="sm"
                            class="px-2 py-0 mr-1"
                            @click.stop="navigateToday"
                            :disabled="isTodayNavDisabled"
                            title="Jump to current month"
                        >
                            Today
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="px-2 py-0"
                            @click.stop="navigateMonths(1)"
                            :disabled="isNextNavDisabled"
                            title="Next month"
                        >
                            &raquo;
                        </b-button>
                    </div>
                </div>

                <!-- Calendar View -->
                <div class="calendar-embed-wrap position-relative">
                    <b-calendar
                        class="time-manager-calendar compact-capacity-calendar"
                        :key="calendarKey"
                        :value="calendarSelectedValue"
                        @input="onCalendarDayPicked"
                        @context="onCalendarContextChange"
                        :block="true"
                        :start-weekday="1"
                        :date-disabled-fn="calendarDisabledDates"
                        :date-info-fn="calendarDayStatusStyle"
                        locale="en"
                        :min="min"
                        :max="max"
                        :initial-date="activeViewDate"
                        hide-header
                    >
                    </b-calendar>
                </div>

                <!-- Day Status Legend -->
                <div class="calendar-day-legend compact-legend mt-2">
                    <span class="legend-item"><span class="legend-swatch table-success"></span>Full</span>
                    <span class="legend-item"><span class="legend-swatch table-warning"></span>Gap</span>
                    <span class="legend-item"><span class="legend-swatch table-danger"></span>Over</span>
                    <span class="legend-item"><span class="legend-swatch table-info"></span>Absence</span>
                    <span class="legend-item"><span class="legend-swatch calendar-legend-weekend"></span>Wknd</span>
                    <span v-if="isMultiMode" class="legend-item">
                        <span class="legend-swatch legend-allocated-swatch"></span>Selected
                    </span>
                </div>

                <!-- Multi-Mode Selected Dates Breakdown List -->
                <div v-if="isMultiMode" class="selected-allocations-breakdown mt-2 border rounded p-2">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="extra-small font-weight-bold text-uppercase text-muted">
                            Selected Dates ({{ localAllocations.length }})
                        </span>
                        <span class="extra-small text-muted">
                            Total: <strong>{{ totalAllocatedHours }}h</strong> / {{ deltaHours }}h
                        </span>
                    </div>

                    <div v-if="localAllocations.length === 0" class="text-center py-2 text-muted extra-small">
                        <em>No dates selected yet. Click empty days above or click <strong>Auto-Fill</strong>.</em>
                    </div>

                    <div v-else class="allocations-scroll-wrap">
                        <div
                            v-for="alloc in localAllocations"
                            :key="alloc.date"
                            class="allocation-item-row d-flex justify-content-between align-items-center py-1 px-1 border-bottom"
                            :class="{ 'over-capacity-row': isAllocDayOverCapacity(alloc) }"
                            data-test="allocation-row"
                            :data-test-date="alloc.date"
                        >
                            <div class="d-flex align-items-center mr-2 text-truncate">
                                <i class="bi bi-calendar-check mr-1 text-success flex-shrink-0"></i>
                                <span class="small font-weight-bold text-truncate">{{ formatAllocationDateLabel(alloc.date) }}</span>
                                <span class="extra-small text-muted ml-1" :title="`Already logged: ${getAllocDayExisting(alloc.date)}h`">
                                    ({{ getAllocDayExisting(alloc.date) }}h)
                                </span>
                            </div>

                            <div class="d-flex align-items-center">
                                <b-input-group size="sm" class="alloc-hours-group">
                                    <b-form-input
                                        type="number"
                                        size="sm"
                                        step="0.25"
                                        min="0.25"
                                        :max="localAllowOvertime ? 24 : capacity"
                                        class="alloc-number-input font-weight-bold text-center"
                                        v-model.number="alloc.hours"
                                        @input="onAllocationHoursChanged(alloc.date, $event)"
                                        data-test="input-allocation-hours"
                                    ></b-form-input>
                                    <template #append>
                                        <b-input-group-text class="px-1 extra-small">h</b-input-group-text>
                                    </template>
                                </b-input-group>

                                <b-button
                                    variant="link"
                                    size="sm"
                                    class="text-danger p-0 ml-2"
                                    @click.stop="removeAllocation(alloc.date)"
                                    title="Remove this date from allocation"
                                    data-test="btn-remove-allocation-date"
                                >
                                    <i class="bi bi-x-circle-fill"></i>
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Single Date Mode Day Capacity Impact Summary -->
                <div v-else class="selected-day-impact-card mt-2 p-2 rounded">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="small font-weight-bold">
                            <i class="bi bi-info-circle mr-1 text-info"></i>
                            {{ previewDateFormatted }}
                        </span>
                        <span v-if="previewDayStatusInfo" class="badge" :class="previewDayStatusInfo.badgeClass">
                            {{ previewDayStatusInfo.hoursText }}
                        </span>
                    </div>

                    <!-- Impact Calculation -->
                    <div class="extra-small impact-detail-text">
                        <div v-if="previewImpact">
                            <span v-if="previewImpact.type === 'ideal'" class="text-success font-weight-bold">
                                <i class="bi bi-check-circle-fill mr-1"></i>Perfect Match! +{{ deltaHours }}h fills daily capacity ({{ previewImpact.newTotal }}h/{{ capacity }}h).
                            </span>
                            <span v-else-if="previewImpact.type === 'gap'" class="text-warning font-weight-bold">
                                <i class="bi bi-hourglass-split mr-1"></i>Capacity available: +{{ deltaHours }}h leaves {{ previewImpact.remainingGap }}h gap ({{ previewImpact.newTotal }}h/{{ capacity }}h).
                            </span>
                            <span v-else-if="previewImpact.type === 'over'" class="text-danger font-weight-bold">
                                <i class="bi bi-exclamation-triangle-fill mr-1"></i>Exceeds capacity by +{{ previewImpact.overHours }}h ({{ previewImpact.newTotal }}h/{{ capacity }}h).
                            </span>
                            <span v-else-if="previewImpact.type === 'empty'" class="text-info">
                                Empty workday: +{{ deltaHours }}h leaves {{ previewImpact.remainingGap }}h open ({{ previewImpact.newTotal }}h/{{ capacity }}h).
                            </span>
                            <span v-else-if="previewImpact.type === 'absence'" class="text-muted">
                                Day marked as absence / leave.
                            </span>
                            <span v-else class="text-muted">
                                {{ previewImpact.message }}
                            </span>
                        </div>
                        <div v-else class="text-muted">
                            Select an unfilled date (yellow) to sync hours without exceeding capacity.
                        </div>
                    </div>
                </div>

                <!-- Popover Footer -->
                <div class="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                    <b-button size="sm" variant="link" class="text-muted p-0 small" @click="closePopover">
                        Cancel
                    </b-button>
                    <b-button
                        v-if="isMultiMode"
                        size="sm"
                        variant="primary"
                        class="px-3 font-weight-bold shadow-sm"
                        :disabled="localAllocations.length === 0"
                        @click="confirmMultiSelection"
                        data-test="btn-apply-allocations"
                    >
                        Apply {{ localAllocations.length }} Date{{ localAllocations.length > 1 ? 's' : '' }} ({{ totalAllocatedHours }}h)
                    </b-button>
                    <b-button
                        v-else
                        size="sm"
                        variant="primary"
                        class="px-3 font-weight-bold shadow-sm"
                        :disabled="!tempSelectedDate"
                        @click="confirmSelection"
                    >
                        Apply Date
                    </b-button>
                </div>
            </div>
        </b-popover>
    </div>
</template>

<script>
import _ from 'lodash';
import { format } from 'date-fns';
import {
    parseLocalDate,
    formatLocalDate,
    isDateWeekend,
    isDateHoliday,
    getDayExistingHours,
    getDayGap,
    autoFillGaps
} from '../reconcileAllocation';

export default {
    name: 'picker-capacity-date',
    props: {
        value: {
            type: [String, Array],
            default: ''
        },
        allocations: {
            type: Array,
            default: () => []
        },
        multi: {
            type: Boolean,
            default: false
        },
        suggestedDate: {
            type: String,
            default: ''
        },
        deltaHours: {
            type: [Number, String],
            default: 0
        },
        dailyCapacity: {
            type: Number,
            default: 8
        },
        calendarDayStatuses: {
            type: Array,
            default: () => []
        },
        publicHolidays: {
            type: [Array, Object, Function],
            default: () => []
        },
        min: {
            type: String,
            default: null
        },
        max: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        allowOvertime: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const initialDate = typeof this.value === 'string' && this.value ? parseLocalDate(this.value) : null;
        const validDate = initialDate || new Date();
        const initialAllocations = Array.isArray(this.allocations) && this.allocations.length
            ? JSON.parse(JSON.stringify(this.allocations))
            : (Array.isArray(this.value) ? JSON.parse(JSON.stringify(this.value)) : []);

        return {
            isPopoverOpen: false,
            tempSelectedDate: typeof this.value === 'string' ? (this.value || '') : '',
            localAllocations: initialAllocations,
            localAllowOvertime: this.allowOvertime || false,
            multiNotice: '',
            activeViewDate: validDate,
            calendarKey: 1,
            internalStatuses: [],
            loadingMonth: false,
            triggerId: 'cap-date-picker-' + Math.random().toString(36).substring(2, 9)
        };
    },
    computed: {
        capacity() {
            return this.dailyCapacity > 0 ? this.dailyCapacity : 8;
        },
        isMultiMode() {
            const delta = parseFloat(this.deltaHours) || 0;
            return this.multi || delta > this.capacity || (this.allocations && this.allocations.length > 1);
        },
        currentAllocations() {
            return (this.allocations && this.allocations.length > 0)
                ? this.allocations
                : (Array.isArray(this.value) ? this.value : this.localAllocations);
        },
        currentTheme() {
            return document.documentElement.getAttribute('data-theme') || 'dark';
        },
        todayYMD() {
            return formatLocalDate(new Date());
        },
        displayDate() {
            return typeof this.value === 'string' ? (this.value || '') : '';
        },
        triggerDisplayLabel() {
            if (!this.isMultiMode) {
                return this.displayDate || 'Pick Date';
            }
            const count = this.currentAllocations.length;
            if (count === 0) {
                return `Allocate +${this.deltaHours}h`;
            }
            const sum = Math.round(this.currentAllocations.reduce((acc, a) => acc + (parseFloat(a.hours) || 0), 0) * 100) / 100;
            return `${count} date${count > 1 ? 's' : ''} (${sum}h/${this.deltaHours}h)`;
        },
        totalAllocatedHours() {
            const sum = this.localAllocations.reduce((acc, a) => acc + (parseFloat(a.hours) || 0), 0);
            return Math.round(sum * 100) / 100;
        },
        remainingHours() {
            const delta = Math.round((parseFloat(this.deltaHours) || 0) * 100) / 100;
            return Math.round(Math.max(0, delta - this.totalAllocatedHours) * 100) / 100;
        },
        percentAllocated() {
            const delta = parseFloat(this.deltaHours) || 0;
            if (delta <= 0) return 100;
            return Math.min(100, Math.round((this.totalAllocatedHours / delta) * 100));
        },
        isAllocationComplete() {
            return this.remainingHours <= 0.001;
        },
        progressVariant() {
            if (this.isAllocationComplete) return 'success';
            const delta = parseFloat(this.deltaHours) || 0;
            if (this.totalAllocatedHours > delta) return 'danger';
            return 'warning';
        },
        gapsAvailableSummary() {
            if (!this.mergedDayStatuses || !this.mergedDayStatuses.length) return null;
            const effMax = this.max || this.todayYMD;
            let effMin = this.min;
            if (!effMin) {
                const d = parseLocalDate(effMax) || new Date();
                d.setDate(d.getDate() - 14);
                effMin = formatLocalDate(d);
            }
            let sum = 0;
            const minP = parseLocalDate(effMin) || new Date();
            const maxP = parseLocalDate(effMax) || new Date();
            const curr = new Date(minP);
            while (curr <= maxP) {
                const ymd = formatLocalDate(curr);
                sum += getDayGap(ymd, this.mergedDayStatuses, this.capacity, this.publicHolidays);
                curr.setDate(curr.getDate() + 1);
            }
            return Math.round(sum * 100) / 100;
        },
        currentMonthYearLabel() {
            if (!this.activeViewDate) return '';
            try {
                return format(this.activeViewDate, 'MMMM yyyy');
            } catch (e) {
                return '';
            }
        },
        mergedDayStatuses() {
            const map = new Map();
            (this.internalStatuses || []).forEach(s => {
                if (!s || !s.date) return;
                const ymd = String(s.date).split('T')[0];
                map.set(ymd, { ...s, date: ymd });
            });
            (this.calendarDayStatuses || []).forEach(s => {
                if (!s || !s.date) return;
                const ymd = String(s.date).split('T')[0];
                const prev = map.get(ymd);
                if (!prev) {
                    map.set(ymd, { ...s, date: ymd });
                } else {
                    const prevHrs = getDayExistingHours(ymd, [prev], this.capacity);
                    const nextHrs = getDayExistingHours(ymd, [s], this.capacity);
                    if (s.style === 'absence' || s.status === 'absence' || s.isAbsence || nextHrs >= prevHrs) {
                        map.set(ymd, { ...prev, ...s, date: ymd });
                    }
                }
            });
            return Array.from(map.values());
        },
        selectedDayBadge() {
            if (this.isMultiMode || !this.value) return null;
            return this.calculateDayBadge(this.value);
        },
        selectedDayHasGap() {
            const badge = this.selectedDayBadge;
            return badge && (badge.type === 'gap' || badge.type === 'empty');
        },
        selectedDayIsFull() {
            const badge = this.selectedDayBadge;
            return badge && badge.type === 'full';
        },
        triggerTitle() {
            if (this.isMultiMode) {
                const count = this.currentAllocations.length;
                return count > 0
                    ? `${count} dates selected (${this.totalAllocatedHours}h of ${this.deltaHours}h)`
                    : `Click to allocate ${this.deltaHours}h across available gaps`;
            }
            if (!this.value) return 'Pick a target date for reconciled hours';
            const b = this.selectedDayBadge;
            return b ? `${this.value} (${b.title})` : this.value;
        },
        previewDateFormatted() {
            const target = this.tempSelectedDate || this.value;
            if (!target) return 'No Date Selected';
            const parsed = parseLocalDate(target);
            if (!parsed) return target;
            try {
                return format(parsed, 'EEE, MMM d, yyyy');
            } catch (e) {
                return target;
            }
        },
        previewDayStatusInfo() {
            const target = this.tempSelectedDate || this.value;
            if (!target) return null;
            return this.calculateDayBadge(target);
        },
        previewImpact() {
            const target = this.tempSelectedDate || this.value;
            if (!target) return null;
            const delta = parseFloat(this.deltaHours) || 0;
            const dayStat = _.find(this.mergedDayStatuses, p => p.date === target);
            const isHol = isDateHoliday(target, this.publicHolidays);
            const isWknd = isDateWeekend(target);

            if (isWknd && !isHol) {
                return { type: 'weekend', message: 'Weekend day — regular hours cannot be placed here.' };
            }
            if (dayStat && (dayStat.style === 'absence' || dayStat.status === 'absence' || dayStat.isAbsence)) {
                return { type: 'absence', message: 'Day has booked absence/leave.' };
            }

            const existingRaw = getDayExistingHours(target, this.mergedDayStatuses, this.capacity);
            const currentLogged = existingRaw === -1 ? 0 : existingRaw;

            const newTotal = Math.round((currentLogged + delta) * 100) / 100;
            const remainingGap = Math.round(Math.max(0, this.capacity - newTotal) * 100) / 100;
            const overHours = Math.round(Math.max(0, newTotal - this.capacity) * 100) / 100;

            if (newTotal === this.capacity) {
                return { type: 'ideal', currentLogged, newTotal, delta };
            }
            if (newTotal < this.capacity) {
                return { type: currentLogged === 0 ? 'empty' : 'gap', currentLogged, newTotal, remainingGap, delta };
            }
            return { type: 'over', currentLogged, newTotal, overHours, delta };
        },
        isPrevNavDisabled() {
            if (!this.min || !this.activeViewDate) return false;
            const minDate = parseLocalDate(this.min);
            if (!minDate) return false;
            minDate.setHours(0, 0, 0, 0);
            const lastOfPrev = new Date(this.activeViewDate.getFullYear(), this.activeViewDate.getMonth(), 0, 23, 59, 59, 999);
            return lastOfPrev < minDate;
        },
        isNextNavDisabled() {
            if (!this.max || !this.activeViewDate) return false;
            const maxDate = parseLocalDate(this.max);
            if (!maxDate) return false;
            maxDate.setHours(23, 59, 59, 999);
            const firstOfNext = new Date(this.activeViewDate.getFullYear(), this.activeViewDate.getMonth() + 1, 1, 0, 0, 0, 0);
            return firstOfNext > maxDate;
        },
        isTodayNavDisabled() {
            if (!this.activeViewDate) return false;
            const now = new Date();
            const isSameMonth = this.activeViewDate.getFullYear() === now.getFullYear() &&
                                this.activeViewDate.getMonth() === now.getMonth();
            if (isSameMonth) return true;
            if (this.max) {
                const maxDate = parseLocalDate(this.max);
                if (maxDate) {
                    maxDate.setHours(23, 59, 59, 999);
                    const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
                    if (firstOfThisMonth > maxDate) return true;
                }
            }
            if (this.min) {
                const minDate = parseLocalDate(this.min);
                if (minDate) {
                    minDate.setHours(0, 0, 0, 0);
                    const lastOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                    if (lastOfThisMonth < minDate) return true;
                }
            }
            return false;
        },
        calendarSelectedValue() {
            if (this.isMultiMode) return '';
            if (!this.tempSelectedDate || !this.activeViewDate) {
                return this.tempSelectedDate || '';
            }
            const picked = parseLocalDate(this.tempSelectedDate);
            const view = this.activeViewDate;
            if (
                picked &&
                !isNaN(view.getTime()) &&
                picked.getFullYear() === view.getFullYear() &&
                picked.getMonth() === view.getMonth()
            ) {
                return this.tempSelectedDate;
            }
            return '';
        }
    },
    watch: {
        value(newVal) {
            if (typeof newVal === 'string') {
                this.tempSelectedDate = newVal || '';
                if (newVal) {
                    const parsed = parseLocalDate(newVal);
                    if (parsed) {
                        this.activeViewDate = parsed;
                    }
                }
            } else if (Array.isArray(newVal)) {
                this.localAllocations = JSON.parse(JSON.stringify(newVal));
            }
        },
        allocations: {
            deep: true,
            handler(newVal) {
                if (Array.isArray(newVal)) {
                    this.localAllocations = JSON.parse(JSON.stringify(newVal));
                }
            }
        },
        isPopoverOpen(val) {
            if (val) {
                this.$nextTick(() => {
                    document.addEventListener('click', this.handleOutsideClick, true);
                });
            } else {
                document.removeEventListener('click', this.handleOutsideClick, true);
            }
        }
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleOutsideClick, true);
    },
    methods: {
        formatChipDate(ymd) {
            const p = parseLocalDate(ymd);
            if (!p) return ymd;
            try {
                return format(p, 'MMM d');
            } catch (e) {
                return ymd;
            }
        },
        formatAllocationDateLabel(ymd) {
            const p = parseLocalDate(ymd);
            if (!p) return ymd;
            try {
                return format(p, 'EEE, MMM d');
            } catch (e) {
                return ymd;
            }
        },
        getAllocDayExisting(ymd) {
            const existing = getDayExistingHours(ymd, this.mergedDayStatuses, this.capacity);
            return existing === -1 ? 0 : existing;
        },
        isAllocDayOverCapacity(alloc) {
            const existing = this.getAllocDayExisting(alloc.date);
            return (existing + (parseFloat(alloc.hours) || 0)) > this.capacity;
        },
        handleOutsideClick(e) {
            if (!this.isPopoverOpen) return;
            const trigger = document.getElementById(this.triggerId);
            if (trigger && (trigger === e.target || trigger.contains(e.target))) {
                return;
            }
            const popovers = document.querySelectorAll('.capacity-calendar-popover');
            for (const pop of popovers) {
                if (pop.contains(e.target)) {
                    return;
                }
            }
            this.closePopover();
        },
        openPopover() {
            if (this.disabled) return;
            if (!this.isPopoverOpen) {
                this.togglePopover();
            }
        },
        togglePopover() {
            if (this.disabled) return;
            this.isPopoverOpen = !this.isPopoverOpen;
            if (this.isPopoverOpen) {
                this.multiNotice = '';
                if (this.isMultiMode) {
                    if (this.allocations && this.allocations.length) {
                        this.localAllocations = JSON.parse(JSON.stringify(this.allocations));
                    } else if (Array.isArray(this.value) && this.value.length) {
                        this.localAllocations = JSON.parse(JSON.stringify(this.value));
                    } else {
                        // Pre-populate gaps automatically using Auto-Fill as per user preference!
                        this.runAutoFillGaps();
                    }
                } else {
                    this.tempSelectedDate = typeof this.value === 'string' ? (this.value || '') : '';
                }

                const parsed = this.tempSelectedDate ? parseLocalDate(this.tempSelectedDate) : null;
                this.activeViewDate = parsed || (this.localAllocations.length ? parseLocalDate(this.localAllocations[this.localAllocations.length - 1].date) : new Date()) || new Date();
                this.calendarKey += 1;
                this.ensureMonthStatusesLoaded(this.activeViewDate);
            }
        },
        closePopover() {
            this.isPopoverOpen = false;
        },
        runAutoFillGaps() {
            const result = autoFillGaps({
                deltaHours: this.deltaHours,
                dailyCapacity: this.capacity,
                calendarDayStatuses: this.mergedDayStatuses,
                publicHolidays: this.publicHolidays,
                minDate: this.min,
                maxDate: this.max || this.todayYMD,
                suggestedDate: this.suggestedDate,
                allowOvertime: this.localAllowOvertime
            });

            this.localAllocations = result.allocations;
            if (result.remainingHours > 0) {
                this.multiNotice = `Available weekday gaps (${result.totalGapsFound}h) were filled. ${result.remainingHours}h remain unallocated. Enable "Allow Overtime" to assign remaining hours.`;
            } else {
                this.multiNotice = '';
            }
            this.calendarKey += 1;
        },
        resetAllocations() {
            this.localAllocations = [];
            this.multiNotice = '';
            this.calendarKey += 1;
        },
        removeAllocation(ymd) {
            this.localAllocations = this.localAllocations.filter(a => a.date !== ymd);
            this.multiNotice = '';
            this.calendarKey += 1;
        },
        onAllocationHoursChanged(ymd, val) {
            const item = this.localAllocations.find(a => a.date === ymd);
            if (item) {
                const numeric = parseFloat(val);
                item.hours = isNaN(numeric) ? 0.25 : Math.max(0.25, Math.round(numeric * 100) / 100);
            }
        },
        onOvertimeToggled() {
            if (this.localAllowOvertime && this.remainingHours > 0) {
                this.runAutoFillGaps();
            }
        },
        confirmSelection() {
            if (this.tempSelectedDate) {
                this.$emit('input', this.tempSelectedDate);
                this.$emit('change', this.tempSelectedDate);
            }
            this.closePopover();
        },
        confirmMultiSelection() {
            this.$emit('update:allocations', this.localAllocations);
            this.$emit('change-allocations', this.localAllocations);
            this.$emit('input', this.localAllocations);
            this.$emit('change', this.localAllocations);
            this.closePopover();
        },
        onCalendarDayPicked(ymd) {
            if (!ymd) return;

            if (this.isMultiMode) {
                const existingIdx = this.localAllocations.findIndex(a => a.date === ymd);
                if (existingIdx >= 0) {
                    // Toggle off: remove
                    this.localAllocations.splice(existingIdx, 1);
                    this.multiNotice = '';
                    this.calendarKey += 1;
                    return;
                }

                // Attempt to add day
                const gap = getDayGap(ymd, this.mergedDayStatuses, this.capacity, this.publicHolidays);
                if (gap <= 0 && !this.localAllowOvertime) {
                    this.multiNotice = `Day ${ymd} has no available capacity gap. Enable "Allow Overtime" to log on this day.`;
                    return;
                }

                let toAdd = 0;
                if (gap > 0) {
                    const rem = this.remainingHours > 0 ? this.remainingHours : this.capacity;
                    toAdd = Math.round(Math.min(rem, gap) * 100) / 100;
                } else if (this.localAllowOvertime) {
                    const rem = this.remainingHours > 0 ? this.remainingHours : this.capacity;
                    toAdd = Math.round(Math.min(rem, this.capacity) * 100) / 100;
                }

                if (toAdd > 0) {
                    this.localAllocations.push({ date: ymd, hours: toAdd });
                    this.localAllocations.sort((a, b) => a.date.localeCompare(b.date));
                    this.multiNotice = '';
                    this.calendarKey += 1;
                }
                return;
            }

            // Single date mode
            this.tempSelectedDate = ymd;
            this.confirmSelection();
        },
        onCalendarContextChange(context) {
            if (context && context.activeDate) {
                const prev = this.activeViewDate;
                const curr = context.activeDate;
                if (!prev || prev.getFullYear() !== curr.getFullYear() || prev.getMonth() !== curr.getMonth()) {
                    this.activeViewDate = curr;
                    this.ensureMonthStatusesLoaded(curr);
                }
            }
        },
        navigateMonths(delta) {
            const current = this.activeViewDate || new Date();
            const next = new Date(current.getFullYear(), current.getMonth() + delta, 1);
            this.activeViewDate = next;
            this.calendarKey += 1;
            this.ensureMonthStatusesLoaded(next);
        },
        navigateToday() {
            const today = new Date();
            this.activeViewDate = today;
            this.calendarKey += 1;
            this.ensureMonthStatusesLoaded(today);
        },
        async ensureMonthStatusesLoaded(dateObj) {
            const y = dateObj.getFullYear();
            const m = dateObj.getMonth();
            const start = new Date(Date.UTC(y, m, 1)).toISOString().split('T')[0];
            const end = new Date(Date.UTC(y, m + 1, 0)).toISOString().split('T')[0];

            const hasAllDates = this.mergedDayStatuses.some(s => s.date >= start && s.date <= end);
            if (hasAllDates) return;

            this.loadingMonth = true;
            try {
                const resp = await this.$http.get('/TimeManagement/GetPickedRangeDayStatuses', {
                    params: { start, end }
                });
                if (resp?.data?.calendarDayStatuses) {
                    this.internalStatuses = _.uniqBy([
                        ...this.internalStatuses,
                        ...resp.data.calendarDayStatuses
                    ], 'date');
                    this.calendarKey += 1;
                }
            } catch (e) {
                // Non-blocking fallback
            } finally {
                this.loadingMonth = false;
            }
        },
        calendarDisabledDates(ymd, date) {
            if (!this.isMultiMode && (ymd === this.todayYMD || ymd === this.tempSelectedDate)) return false;
            if (this.isMultiMode && this.localAllocations.some(a => a.date === ymd)) return false;

            const parsed = parseLocalDate(ymd);
            const day = date ? date.getDay() : (parsed ? parsed.getDay() : 0);
            const isWeekend = day === 0 || day === 6;
            const isHoliday = isDateHoliday(ymd, this.publicHolidays);

            if (isWeekend && !isHoliday && !this.localAllowOvertime) return true;
            if (this.min && ymd < this.min) return true;
            if (this.max && ymd > this.max) return true;
            return false;
        },
        calendarDayStatusStyle(ymd) {
            let statusClass = '';
            const theDate = _.find(this.mergedDayStatuses, p => p.date === ymd);

            if (theDate) {
                if (theDate.style === 'absence' || theDate.status === 'absence' || theDate.isAbsence) {
                    statusClass = 'table-info';
                } else if (theDate.style === 'done' || theDate.status === 'completed') {
                    statusClass = 'table-success';
                } else if (theDate.style === 'pending' || theDate.status === 'incomplete') {
                    statusClass = 'table-warning';
                } else if (theDate.style === 'overflaw' || theDate.status === 'overflaw') {
                    statusClass = 'table-danger';
                }
            }

            if (!statusClass && isDateHoliday(ymd, this.publicHolidays)) {
                statusClass = 'table-info';
            }

            const isWknd = isDateWeekend(ymd);
            if (!statusClass && isWknd) {
                statusClass = 'bg-light';
            }

            const classes = [];
            if (statusClass) {
                classes.push(statusClass);
            }
            if (ymd === this.todayYMD) {
                classes.push('cal-today');
            }
            if (!this.isMultiMode && ymd === this.tempSelectedDate) {
                classes.push('cal-selected-target');
            }
            if (this.isMultiMode && this.localAllocations.some(a => a.date === ymd)) {
                classes.push('cal-allocated-day');
            }
            return classes.join(' ');
        },
        calculateDayBadge(ymd) {
            const dayStat = _.find(this.mergedDayStatuses, p => p.date === ymd);
            const isHol = isDateHoliday(ymd, this.publicHolidays);
            const isWknd = isDateWeekend(ymd);

            if (isHol) {
                return { type: 'holiday', text: 'Holiday', badgeClass: 'badge-info', title: 'Public Holiday' };
            }
            if (dayStat && (dayStat.style === 'absence' || dayStat.status === 'absence' || dayStat.isAbsence)) {
                return { type: 'absence', text: 'Absence', badgeClass: 'badge-info', title: 'Absence / Leave booked' };
            }
            if (isWknd) {
                return { type: 'weekend', text: 'Weekend', badgeClass: 'badge-secondary', title: 'Weekend' };
            }

            const existingRaw = getDayExistingHours(ymd, this.mergedDayStatuses, this.capacity);
            const hours = existingRaw === -1 ? 0 : existingRaw;

            const gap = Math.round(Math.max(0, this.capacity - hours) * 100) / 100;

            if (hours >= this.capacity) {
                if (hours > this.capacity) {
                    return {
                        type: 'over',
                        text: `${hours}h / ${this.capacity}h (+${hours - this.capacity}h)`,
                        hoursText: `${hours}h / ${this.capacity}h Logged`,
                        badgeClass: 'badge-danger',
                        title: 'Over daily capacity'
                    };
                }
                return {
                    type: 'full',
                    text: `${this.capacity}h / ${this.capacity}h Full`,
                    hoursText: `${this.capacity}h / ${this.capacity}h Full`,
                    badgeClass: 'badge-success',
                    title: 'Full capacity reached'
                };
            }

            if (hours === 0) {
                return {
                    type: 'empty',
                    text: `0h / ${this.capacity}h Empty`,
                    hoursText: `0h / ${this.capacity}h Logged`,
                    badgeClass: 'badge-secondary',
                    title: `0h logged (Gap: ${this.capacity}h available)`
                };
            }

            return {
                type: 'gap',
                text: `${hours}h / ${this.capacity}h (Gap: ${gap}h)`,
                hoursText: `${hours}h / ${this.capacity}h Logged`,
                badgeClass: 'badge-warning',
                title: `Time gap: ${gap}h capacity available`
            };
        }
    }
};
</script>

<style scoped>
.capacity-date-picker-wrap {
    min-width: 175px;
    max-width: 260px;
}

.capacity-date-trigger-btn {
    height: 31px;
    padding: 0.2rem 0.5rem;
    font-size: 0.8rem;
    background-color: var(--vt-input-bg, #fff);
    border-color: var(--vt-border, #ced4da);
    color: var(--vt-text-color, #212529);
    transition: all 0.15s ease-in-out;
}

.capacity-date-trigger-btn:hover {
    border-color: #0284c7;
    background-color: rgba(2, 132, 199, 0.05);
}

.date-label {
    font-size: 0.78rem;
    letter-spacing: 0.01em;
}

.capacity-badge-pill {
    font-size: 0.65rem;
    font-weight: 700;
    padding: 0.2rem 0.45rem;
}

.mini-allocations-chips {
    line-height: 1;
}

.mini-alloc-chip {
    font-size: 0.68rem;
    padding: 0.15rem 0.35rem;
    border-color: var(--vt-border, #dee2e6) !important;
}

/* Popover Content Styling */
.capacity-calendar-container {
    width: 320px;
    background-color: var(--vt-card-bg, #ffffff);
    color: var(--vt-text-color, #1e293b);
}

.capacity-calendar-container.multi-calendar-container {
    width: 440px;
    max-width: 92vw;
}

[data-theme="dark"] .capacity-calendar-container {
    background-color: #0f172a;
    color: #f1f5f9;
}

.active-month-label {
    font-size: 0.88rem;
    color: var(--vt-text-color, #0f172a);
}

[data-theme="dark"] .active-month-label {
    color: #38bdf8;
}

/* Multi-Mode Progress Box */
.allocation-progress-card {
    background: rgba(2, 132, 199, 0.06);
    border: 1px solid rgba(2, 132, 199, 0.15);
}

[data-theme="dark"] .allocation-progress-card {
    background: rgba(15, 23, 42, 0.85);
    border-color: rgba(56, 189, 248, 0.25);
}

/* Breakdown List */
.selected-allocations-breakdown {
    background-color: var(--vt-card-bg, #fff);
    border-color: var(--vt-border, #dee2e6) !important;
}

[data-theme="dark"] .selected-allocations-breakdown {
    background-color: rgba(255, 255, 255, 0.03);
}

.allocations-scroll-wrap {
    max-height: 140px;
    overflow-y: auto;
}

.allocation-item-row {
    border-color: var(--vt-border, #f1f5f9) !important;
}

.alloc-hours-group {
    width: 72px;
}

.alloc-number-input {
    font-size: 0.78rem;
    padding: 0.15rem 0.25rem;
    height: 26px;
}

.over-capacity-row {
    background-color: rgba(220, 53, 69, 0.08);
}

/* Hide default calendar nav header to use custom compact navbar */
.time-manager-calendar ::v-deep .b-calendar-nav {
    display: none !important;
}

/* Calendar Day Cell Styles matching Timelog */
.time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn {
    position: relative;
    box-shadow: 0 0 0 2px #0284c7, 0 0 8px rgba(2, 132, 199, 0.4) !important;
    font-weight: 800 !important;
}

[data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-today .btn {
    box-shadow: 0 0 0 2px #38bdf8, 0 0 10px rgba(56, 189, 248, 0.55) !important;
}

.time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-selected-target .btn {
    box-shadow: 0 0 0 2px #f59e0b, 0 0 8px rgba(245, 158, 11, 0.6) !important;
}

.time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-allocated-day .btn {
    background-color: rgba(40, 167, 69, 0.25) !important;
    border: 2px solid #16a34a !important;
    font-weight: 800 !important;
}

[data-theme="dark"] .time-manager-calendar ::v-deep .b-calendar-grid-body .col.cal-allocated-day .btn {
    background-color: rgba(34, 197, 94, 0.3) !important;
    border: 2px solid #22c55e !important;
    color: #ffffff !important;
}

.compact-capacity-calendar ::v-deep .btn {
    font-size: 0.8rem !important;
    padding: 0.25rem !important;
}

/* Legend Swatches */
.calendar-day-legend.compact-legend {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 0.4rem 0.8rem;
    font-size: 0.72rem;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
}

.legend-swatch {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 2px;
    display: inline-block;
    padding: 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
}

.legend-swatch.table-success {
    background-color: #16a34a !important;
}
.legend-swatch.table-warning {
    background-color: #d97706 !important;
}
.legend-swatch.table-danger {
    background-color: #dc2626 !important;
}
.legend-swatch.table-info {
    background-color: #0284c7 !important;
}
.calendar-legend-weekend {
    background-color: #64748b !important;
}
.legend-allocated-swatch {
    background-color: #16a34a;
    border: 2px solid #15803d;
}

/* Selected Day Impact Box */
.selected-day-impact-card {
    background: rgba(2, 132, 199, 0.06);
    border: 1px solid rgba(2, 132, 199, 0.15);
}

[data-theme="dark"] .selected-day-impact-card {
    background: rgba(15, 23, 42, 0.85);
    border-color: rgba(56, 189, 248, 0.25);
}

.impact-detail-text {
    line-height: 1.4;
    font-size: 0.75rem;
}

[data-theme="dark"] .capacity-date-trigger-btn {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.15);
    color: #e2e8f0;
}

[data-theme="dark"] .capacity-date-trigger-btn:hover {
    background-color: rgba(56, 189, 248, 0.12);
    border-color: #38bdf8;
    color: #ffffff;
}

.text-warning-contrast {
    color: #d97706 !important;
}

[data-theme="dark"] .text-warning-contrast {
    color: #fbbf24 !important;
}
</style>

<style>
.popover.capacity-calendar-popover {
    max-width: 480px !important;
    z-index: 1080 !important;
    border-radius: 8px;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.25) !important;
}

.popover.capacity-calendar-popover .popover-body {
    padding: 0 !important;
    border-radius: 8px;
    overflow: hidden;
}
</style>
